import { useMutation, useQuery } from '@tanstack/react-query'

import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

import { useNames } from './names'
import { useFindProfile } from './profile'

interface IComment {
  comment: string
  createdOn: Date
  id: number
  user: {
    lastName: string
    firstName: string
  }
}
interface IPostTaskComment {
  comment: string
  taskId: number
  isSapComment: boolean
}

export function useAddTaskComment() {
  const { data: user } = useFindProfile()
  const { divideName } = useNames()
  const { firstName, lastName } = divideName(user?.displayName ?? '')

  const siteId = user && user.selectedSite.id

  async function postTaskComment({
    comment,
    taskId,
    isSapComment,
  }: IPostTaskComment) {
    const url = `sites/${siteId}/tasks/${taskId}/comments`
    const { data } = await api.post<IComment>(url, { comment, isSapComment })
    return data
  }

  return useMutation({
    mutationFn: postTaskComment,
    onMutate: handleOptimisticTaskComment,
    onError: (_err, _newTask, context) => {
      if (!context) return
      queryClient.setQueryData(context.commentsKey, context.previousComment)
    },
    onSettled: (_tasks, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.commentsKey,
      }),
  })

  async function handleOptimisticTaskComment({
    taskId,
    comment,
    isSapComment,
  }: IPostTaskComment) {
    if (isSapComment) return

    await queryClient.cancelQueries({ queryKey: ['COMMENTS', taskId] })

    const [commentsQuery] = queryClient.getQueriesData<IComment[]>({
      queryKey: ['COMMENTS', taskId],
      type: 'active',
    })

    const [commentsKey, commentsResult] = commentsQuery

    if (!commentsKey || !commentsResult) return

    const previousComment = commentsResult

    const newComment = {
      comment,
      createdOn: new Date(),
      id: -1,
      user: {
        firstName,
        lastName,
      },
    }

    const updatedComments = [...commentsResult, newComment]
    queryClient.setQueryData(commentsKey, () => updatedComments)

    return { commentsKey, updatedComments, previousComment }
  }
}

export function useFindTaskComments(taskId: number) {
  const { data: user } = useFindProfile()
  const siteId = user && user.selectedSite.id

  async function fetchTaskComments() {
    const url = `sites/${siteId}/tasks/${taskId}/comments`
    const { data } = await api.get<IComment[]>(url)
    return data
  }

  return useQuery({
    queryFn: fetchTaskComments,
    queryKey: ['COMMENTS', taskId],
  })
}
